/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Wand2 } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useTranslation } from "react-i18next";
import { MagicFillFormType, MagicFillMetadata } from "./MagicFillButton";
import { requestMagicFill, type MagicFillResult } from "./magicFillRequest";
import { canUseMagicFill } from "./canUseMagicFill";
import { useMagicFill } from "@/shared/contexts/MagicFillContext";

interface MagicFillFieldButtonProps {
  formType: MagicFillFormType;
  fieldKey: string;
  getCurrentValues: () => Record<string, any>;
  onApplySuggestion: (value: any) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  metadata?: MagicFillMetadata;
  className?: string;
}

export const MagicFillFieldButton = ({
  formType,
  fieldKey,
  getCurrentValues,
  onApplySuggestion,
  onLoadingChange,
  metadata,
  className,
}: MagicFillFieldButtonProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { setFieldLoading } = useMagicFill();

  const updateLoadingState = (loading: boolean) => {
    setIsLoading(loading);
    setFieldLoading(fieldKey, loading); // Atualiza loading apenas para este campo específico
    onLoadingChange?.(loading);
  };
  const { user } = useAuth();
  const { i18n } = useTranslation();

  const isEnglish = i18n.language?.startsWith("en");

  if (!canUseMagicFill(formType, user)) {
    return null;
  }

  const TIMEOUT_MS = 10000;
  const MAX_RETRIES = 2;

  const executeRequest = (
    fields: Record<string, any>,
    signal: AbortSignal
  ): Promise<MagicFillResult> =>
    requestMagicFill({
      formType,
      fields,
      metadata: { ...(metadata || {}), fieldKey },
      signal,
      language: i18n.language || "pt-BR",
    });

  const handleClick = async () => {
    updateLoadingState(true);
    let lastError: Error | null = null;

    const currentValues = getCurrentValues() || {};

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const abortController = new AbortController();
      const timeoutId = window.setTimeout(() => {
        abortController.abort();
      }, TIMEOUT_MS);

      try {
        const response = await executeRequest(currentValues, abortController.signal);

        window.clearTimeout(timeoutId);

        if (!response?.suggestedFields) {
          toast({
            variant: "destructive",
            title: isEnglish
              ? "Unable to suggest value"
              : "Não foi possível sugerir valor",
            description: isEnglish
              ? "Please try again in a few moments."
              : "Tente novamente em alguns instantes.",
          });
          updateLoadingState(false);
          return;
        }

        const suggestedValue = response.suggestedFields[fieldKey];

        if (suggestedValue === undefined || suggestedValue === null || suggestedValue === "") {
          toast({
            title: isEnglish ? "No suggestion" : "Sem sugestão",
            description: isEnglish
              ? "AI could not generate a suggestion for this field."
              : "A IA não conseguiu gerar uma sugestão para este campo.",
          });
          updateLoadingState(false);
          return;
        }

        onApplySuggestion(suggestedValue);

        // Toast removido para preenchimento individual mais silencioso
        updateLoadingState(false);
        return;
      } catch (error: any) {
        window.clearTimeout(timeoutId);
        lastError = error;

        if (error?.name === "AbortError" && attempt < MAX_RETRIES) {
          continue;
        }

        if (error?.name !== "AbortError") {
          break;
        }
      }
    }

    if (lastError?.name === "AbortError") {
      toast({
        variant: "destructive",
        title: isEnglish ? "Timeout exceeded" : "Tempo limite excedido",
        description: isEnglish
          ? "Auto-fill took too long after multiple attempts. Please try again later."
          : "O preenchimento automático demorou muito após várias tentativas. Tente novamente mais tarde.",
      });
    } else if (lastError) {
      const message =
        (lastError as any)?.response?.data?.message ||
        lastError?.message ||
        (isEnglish
          ? "Error while requesting auto-fill."
          : "Erro ao consultar o preenchimento automático.");

      toast({
        variant: "destructive",
        title: isEnglish ? "Error" : "Erro",
        description: message,
      });
    }

    updateLoadingState(false);
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleClick}
            disabled={isLoading}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 ${className || ""}`}
          >
            <Wand2 className={`h-4 w-4 ${isLoading ? "animate-pulse text-primary" : ""}`} />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isEnglish ? "Fill with AI" : "Preencher com IA"}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
